import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Modal,
  Select,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { addInventory } from '../../Services/FirebaseUtils';
import { FiUpload } from "react-icons/fi";
import CenterForm from './CenterForm';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { storage, db } from '../../firebase/config';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 1,
  maxHeight: '90vh',
  overflow: 'auto'
};

const initialFormState = {
  operatorId: '',
  operatorName: '',
  centerId: '',
  centerName: '',
  inventoryName: '',
  size: '',
  floor: '',
  active: false,
  lastUpdatedDate: '',
  cabinNumber: '',
  inventoryType: '',
  quantity: '',
  availabilityDate: '',
  description: '',
  city: '',
  locality: '',
  micromarket: '',
  address: '',
  googleMapsUrl: '',
  centerDetails: '',
  inventoryDetails: '',
  section: '',
  pricePerHour: '',
  pricePerDay: '',
  creditsPerHour: '', 
  creditsPerDay: '', 
  photos: [],
};

const photoTypes = [
  'Breakout Area',
  'Conference Room',
  'Manager Cabin',
  'Phone Booth',
  'Private Cabin',
  'Representative Breakout Area',
  'Representative Conference Room',
  'Representative Manager Cabin',
  'Representative Phone Booth',
  'Representative Private Cabin',
  'Layout'
];

const InventoryForm = ({
  open,
  onClose,
  centers = [],
  operators = [],
  theme,
  onSubmit,
  initialData,
  importInitialData,
  editMode
}) => {
  const [openCenterForm, setOpenCenterForm] = useState(false);

  // Choose which initial data to use - prioritize importInitialData
  const formInitialData = importInitialData || initialData;
  console.log('InventoryForm formInitialData:', formInitialData);

  const [formData, setFormData] = useState({
    ...initialFormState,
    operatorId: formInitialData?.operatorId || '',
    operatorName: formInitialData?.operatorName || '',
    centerId: formInitialData?.centerId || '',
    centerName: formInitialData?.centerName || '',
    pricePerHour: formInitialData?.pricePerHour || '',  // New field for meeting space
    pricePerDay: formInitialData?.pricePerDay || '',   // New field for meeting space
    creditsPerHour: formInitialData?.creditsPerHour || '', // New field for meeting room
    creditsPerDay: formInitialData?.creditsPerDay || '',  // New field for meeting room
    googleMapsLocation: formInitialData?.googleMapsLocation || ''
  });

  console.log('InventoryForm formData:', formData);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photos, setPhotos] = useState([{ type: '', file: null, description: '' }]);
  const [filteredCenters, setFilteredCenters] = useState(centers);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    console.log('InventoryForm editMode:', editMode);
    console.log('InventoryForm formInitialData:', formInitialData);

    if (editMode && formInitialData) {
      // Helper function to safely format date
      const formatDate = (dateStr) => {
        if (!dateStr) return new Date().toISOString().split('T')[0];

        // If it's already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return dateStr;
        }

        // Handle DD/MM/YYYY format
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
          const [day, month, year] = dateStr.split('/');
          return `${year}-${month}-${day}`;
        }

        try {
          // Try to parse the date string
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) {
            console.error('Invalid date:', dateStr);
            return new Date().toISOString().split('T')[0];
          }
          return date.toISOString().split('T')[0];
        } catch (e) {
          console.error('Error parsing date:', dateStr, e);
          return new Date().toISOString().split('T')[0];
        }
      };

      // Format dates safely
      const availDate = formatDate(formInitialData.availabilityDate);
      const lastUpdateDate = formatDate(formInitialData.lastUpdatedDate);

      console.log('Formatted dates:', {
        original: formInitialData.availabilityDate,
        formatted: availDate
      });

      // Initialize photos from formInitialData
      if (formInitialData.photos && formInitialData.photos.length > 0) {
        setPhotos(formInitialData.photos.map(photo => ({
          type: photo.type || photo.name || '',
          description: photo.description || '',
          url: photo.url || '',
          file: null
        })));
      } else {
        setPhotos([{ type: '', file: null, description: '' }]);
      }

      setFormData({
        operatorId: formInitialData.operatorId || '',
        operatorName: formInitialData.operatorName || '',
        centerId: formInitialData.centerId || '',
        centerName: formInitialData.centerName || '',
        inventoryName: formInitialData.inventoryName || '',
        size: formInitialData.size || '',
        floor: formInitialData.floor || '',
        active: formInitialData.active || false,
        lastUpdatedDate: lastUpdateDate,
        cabinNumber: formInitialData.cabinNumber || '',
        inventoryType: formInitialData.inventoryType || '',
        quantity: formInitialData.quantity || '1',
        availabilityDate: availDate,
        description: formInitialData.inventoryDescription || formInitialData.description || '',
        city: formInitialData.city || '',
        locality: formInitialData.locality || '',
        micromarket: formInitialData.micromarket || '',
        address: formInitialData.address || '',
        googleMapsUrl: formInitialData.googleMapsLocation || '',
        centerDetails: formInitialData.centerDetails || '',
        inventoryDetails: formInitialData.inventoryDetails || '',
        section: formInitialData.section || '',
        pricePerHour: formInitialData.pricePerHour || '',  // New field for meeting space
        pricePerDay: formInitialData.pricePerDay || '',   // New field for meeting space
        creditsPerHour: formInitialData.creditsPerHour || '', // New field for meeting room
        creditsPerDay: formInitialData.creditsPerDay || '',  // New field for meeting room
        photos: formInitialData.photos || []
      });
    } else {
      setFormData({
        ...initialFormState,
        lastUpdatedDate: new Date().toISOString().split('T')[0],
        availabilityDate: new Date().toISOString().split('T')[0],
        quantity: '1'
      });
      setPhotos([{ type: '', file: null, description: '' }]);
    }
  }, [editMode, formInitialData]);

  useEffect(() => {
    if (formData.operatorId) {
      const newFilteredCenters = centers.filter(center => center.operatorId === formData.operatorId);
      setFilteredCenters(newFilteredCenters);

      // Clear center selection if the selected center doesn't belong to the new operator
      if (formData.centerId) {
        const centerExists = newFilteredCenters.some(center => center.id === formData.centerId);
        if (!centerExists) {
          setFormData(prev => ({
            ...prev,
            centerName: '',
            centerId: '',
            city: '',
            locality: '',
            micromarket: '',
            address: '',
            googleMapsUrl: '',
            centerDetails: ''
          }));
        }
      }
    } else {
      setFilteredCenters([]);
    }
  }, [formData.operatorId, centers]);

  const handleReset = () => {
    if (!editMode) {
      setFormData({
        ...initialFormState,
        lastUpdatedDate: new Date().toISOString().split('T')[0],
        availabilityDate: new Date().toISOString().split('T')[0],
        quantity: '1'
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for operator selection
    if (name === 'operatorId') {
      const selectedOperator = operators.find(op => op.id === value);
      setFormData(prev => ({
        ...prev,
        operatorId: value,
        operatorName: selectedOperator?.operatorName || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.inventoryName?.trim()) {
      newErrors.inventoryName = "This field is . Enter a value";
    }
    if (!formData.inventoryType) {
      newErrors.inventoryType = "This field is mandatory. Enter a value";
    }
    if (!formData.size) {
      newErrors.size = "This field is mandatory. Enter a value";
    }
    if (!formData.quantity) {
      newErrors.quantity = "This field is mandatory. Enter a value";
    }
    if (!formData.availabilityDate) {
      newErrors.availabilityDate = "This field is mandatory. Enter a value";
    }
    if (!formData.lastUpdatedDate) {
      newErrors.lastUpdatedDate = "This field is mandatory. Enter a value";
    }
    if (!formData.centerName) {
      newErrors.centerName = "This field is mandatory. Enter a value";
    }
    if (!formData.operatorId) {
      newErrors.operatorName = "This field is mandatory. Enter a value";
    }
    if (!formData.locality) {
      newErrors.locality = "This field is mandatory. Enter a value";
    }
    if (!formData.city) {
      newErrors.city = "This field is mandatory. Enter a value";
    }
    if (!formData.micromarket) {
      newErrors.micromarket = "This field is mandatory. Enter a value";
    }
    // if (!formData.googleMapsUrl) {
    //   newErrors.googleMapsUrl = "This field is mandatory. Enter a value";
    // }

    if (formData.inventoryType === 'Meeting Space') {
      if (!formData.pricePerHour) {
        newErrors.pricePerHour = "This field is mandatory. Enter a value";
      }
      if (!formData.pricePerDay) {
        newErrors.pricePerDay = "This field is mandatory. Enter a value";
      }
      if (!formData.creditsPerHour) {
        newErrors.creditsPerHour = "This field is mandatory. Enter a value";
      }
      if (!formData.creditsPerDay) {
        newErrors.creditsPerDay = "This field is mandatory. Enter a value";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare photos array with both existing and new photos
      const uploadedPhotoUrls = await Promise.all(
        photos.map(async (photo) => {
          // If photo already has a URL and no new file, keep the existing photo
          if (photo.url && !photo.file) {
            return {
              type: photo.type || '',
              description: photo.description || '',
              url: photo.url
            };
          }

          // If there's a new file, upload it
          if (photo.file) {
            const uniqueFilename = `${uuidv4()}_${photo.file.name}`;
            const storageRef = ref(storage, `inventory_photos/${uniqueFilename}`);

            try {
              const uploadResult = await uploadBytes(storageRef, photo.file);
              const downloadURL = await getDownloadURL(uploadResult.ref);

              return {
                type: photo.type || '',
                description: photo.description || '',
                url: downloadURL
              };
            } catch (error) {
              console.error('Error uploading photo:', photo.file.name, error);
              return null;
            }
          }

          return null;
        })
      );

      // Filter out null values and empty photos
      const validPhotos = uploadedPhotoUrls.filter(
        (photo) => photo !== null && photo.url
      );

      const updatedInventoryFormData = {
        ...formData,
        photos: validPhotos,
        availabilityDate: formData.availabilityDate || new Date().toISOString().split('T')[0],
        lastUpdatedDate: formData.lastUpdatedDate || new Date().toISOString().split('T')[0]
      };

      console.log('Submitting form with data:', updatedInventoryFormData);

      if (typeof onSubmit === 'function') {
        await onSubmit(updatedInventoryFormData);
        handleClose();
      } else {
        console.error('onSubmit is not a function:', onSubmit);
      }

    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleCenterSelect = (event, newValue) => {
    console.log('Selected center:', newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        centerName: newValue.centerName || '',
        centerId: newValue.id || '',
        city: newValue.city || '',
        locality: newValue.locality || '',
        micromarket: newValue.micromarket || '',
        address: newValue.address || '',
        googleMapsUrl: newValue.googleMapsLocation || '',
        centerDetails: newValue.centerDetails || ''
      }));
    } else {
      // Clear center-related fields if no center is selected
      setFormData(prev => ({
        ...prev,
        centerName: '',
        centerId: '',
        city: '',
        locality: '',
        micromarket: '',
        address: '',
        googleMapsUrl: '',
        centerDetails: ''
      }));
    }
  };

  const handleOperatorSelect = (event, newValue) => {
    console.log('Selected operator:', newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        operatorName: newValue.operatorName || '',
        operatorId: newValue.id || ''
      }));
    } else {
      // Clear operator-related fields if no operator is selected
      setFormData(prev => ({
        ...prev,
        operatorName: '',
        operatorId: ''
      }));
    }
  };

  const handlePhotoChange = (index, field, value) => {
    setPhotos(prev => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleRemovePhoto = (index) => {
    setPhotos(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="inventory-modal-title"
      >
        <Box sx={modalStyle}>
          <Box sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #E5E7EB',
            marginBottom: 2
          }}>
            <Typography variant="h5" component="h2">
              {editMode ? 'Edit Inventory' : 'New Inventory'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Inventory Details
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Operator <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                  </Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={operators.sort((a, b) =>
                      a.operatorName.localeCompare(b.operatorName)
                    )}
                    getOptionLabel={(option) => option.operatorName || ''}
                    value={
                      operators.find(
                        (operator) => operator.id === formData.operatorId
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      handleOperatorSelect(event, newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="-Select-"
                        error={!!errors.operatorName}
                        helperText={errors.operatorName}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Center <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                  </Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={filteredCenters.sort((a, b) =>
                      a.centerName.localeCompare(b.centerName)
                    )}
                    getOptionLabel={(option) => option.centerName || ''}
                    value={
                      filteredCenters.find(
                        (center) => center.id === formData.centerId
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      handleCenterSelect(event, newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={formData.operatorId ? "-Select-" : "Please select an operator first"}
                        error={!!errors.centerName}
                        helperText={errors.centerName}
                        disabled={!formData.operatorId}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Inventory Name *"
                    value={formData.inventoryName}
                    onChange={(e) => {
                      setFormData({ ...formData, inventoryName: e.target.value });
                      if (errors.inventoryName) {
                        setErrors({ ...errors, inventoryName: '' });
                      }
                    }}
                    error={!!errors.inventoryName}
                    placeholder="Enter inventory name"
                  />
                  {errors.inventoryName && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.inventoryName}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Cabin Number"
                    value={formData.cabinNumber}
                    onChange={(e) => setFormData({ ...formData, cabinNumber: e.target.value })}
                    placeholder="Enter cabin number"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Size *"
                    type="number"
                    value={formData.size}
                    onChange={(e) => {
                      setFormData({ ...formData, size: e.target.value });
                      if (errors.size) {
                        setErrors({ ...errors, size: '' });
                      }
                    }}
                    error={!!errors.size}
                    placeholder="Enter size"
                  />
                  {errors.size && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.size}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth error={!!errors.inventoryType}>
                    <InputLabel>Inventory Type *</InputLabel>
                    <Select
                      value={formData.inventoryType}
                      label="Inventory Type"
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          inventoryType: e.target.value,
                          // Reset pricing fields when changing type
                          pricePerHour: '',
                          pricePerDay: '',
                          creditsPerHour: '',
                          creditsPerDay: ''
                        });
                        if (errors.inventoryType) {
                          setErrors({ ...errors, inventoryType: '' });
                        }
                      }}
                    >
                      <MenuItem value="Private Cabin">Private Cabin</MenuItem>
                      <MenuItem value="Desk">Desk</MenuItem>
                      <MenuItem value="Meeting Space">Meeting Space</MenuItem>
                      <MenuItem value="Warm Shell">Warm Shell</MenuItem>
                    </Select>
                    {errors.inventoryType && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                        {errors.inventoryType}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Floor"
                    value={formData.floor}
                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                    placeholder="Enter floor"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => {
                      setFormData({ ...formData, quantity: e.target.value });
                      if (errors.quantity) {
                        setErrors({ ...errors, quantity: '' });
                      }
                    }}
                    error={!!errors.quantity}
                    placeholder="Enter quantity"
                  />
                  {errors.quantity && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.quantity}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Availability Date *"
                    type="date"
                    value={formData.availabilityDate}
                    onChange={(e) => {
                      setFormData({ ...formData, availabilityDate: e.target.value });
                      if (errors.availabilityDate) {
                        setErrors({ ...errors, availabilityDate: '' });
                      }
                    }}
                    error={!!errors.availabilityDate}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {errors.availabilityDate && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.availabilityDate}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Updated Date *"
                    type="date"
                    value={formData.lastUpdatedDate}
                    onChange={(e) => {
                      setFormData({ ...formData, lastUpdatedDate: e.target.value });
                      if (errors.lastUpdatedDate) {
                        setErrors({ ...errors, lastUpdatedDate: '' });
                      }
                    }}
                    error={!!errors.lastUpdatedDate}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {errors.lastUpdatedDate && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.lastUpdatedDate}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        sx={{
                          color: theme?.palette.primary.main,
                          '&.Mui-checked': {
                            color: theme?.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="subtitle2" color="text.secondary">
                        Active <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                      </Typography>
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Grid>

                {formData.inventoryType === 'Meeting Space' && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                      Pricing & Credits Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Price Per Hour"
                          type="number"
                          value={formData.pricePerHour}
                          onChange={(e) => {
                            setFormData({ ...formData, pricePerHour: e.target.value });
                            if (errors.pricePerHour) {
                              setErrors({ ...errors, pricePerHour: '' });
                            }
                          }}
                          error={!!errors.pricePerHour}
                          placeholder="Enter price per hour"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            inputProps: { min: 0 }
                          }}
                        />
                        {errors.pricePerHour && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                            {errors.pricePerHour}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Price Per Day"
                          type="number"
                          value={formData.pricePerDay}
                          onChange={(e) => {
                            setFormData({ ...formData, pricePerDay: e.target.value });
                            if (errors.pricePerDay) {
                              setErrors({ ...errors, pricePerDay: '' });
                            }
                          }}
                          error={!!errors.pricePerDay}
                          placeholder="Enter price per day"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            inputProps: { min: 0 }
                          }}
                        />
                        {errors.pricePerDay && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                            {errors.pricePerDay}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Credits Per Hour"
                          type="number"
                          value={formData.creditsPerHour}
                          onChange={(e) => {
                            setFormData({ ...formData, creditsPerHour: e.target.value });
                            if (errors.creditsPerHour) {
                              setErrors({ ...errors, creditsPerHour: '' });
                            }
                          }}
                          error={!!errors.creditsPerHour}
                          placeholder="Enter credits per hour"
                          InputProps={{
                            inputProps: { min: 0 }
                          }}
                        />
                        {errors.creditsPerHour && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                            {errors.creditsPerHour}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Credits Per Day"
                          type="number"
                          value={formData.creditsPerDay}
                          onChange={(e) => {
                            setFormData({ ...formData, creditsPerDay: e.target.value });
                            if (errors.creditsPerDay) {
                              setErrors({ ...errors, creditsPerDay: '' });
                            }
                          }}
                          error={!!errors.creditsPerDay}
                          placeholder="Enter credits per day"
                          InputProps={{
                            inputProps: { min: 0 }
                          }}
                        />
                        {errors.creditsPerDay && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                            {errors.creditsPerDay}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Center Details
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={formData.city}
                    onChange={(e) => {
                      setFormData({ ...formData, city: e.target.value });
                      if (errors.city) {
                        setErrors({ ...errors, city: '' });
                      }
                    }}
                    error={!!errors.city}
                    placeholder="Enter city"
                  />
                  {errors.city && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.city}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Locality"
                    value={formData.locality}
                    onChange={(e) => {
                      setFormData({ ...formData, locality: e.target.value });
                      if (errors.locality) {
                        setErrors({ ...errors, locality: '' });
                      }
                    }}
                    error={!!errors.locality}
                    placeholder="Enter locality"
                  />
                  {errors.locality && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.locality}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    rows={3}
                    value={formData.address}
                    disabled
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Micromarket"
                    value={formData.micromarket}
                    disabled
                  // onChange={(e) => {
                  //   setFormData({ ...formData, micromarket: e.target.value });
                  //   if (errors.micromarket) {
                  //     setErrors({ ...errors, micromarket: '' });
                  //   }
                  // }}
                  // error={!!errors.micromarket}
                  // placeholder="Enter micromarket"
                  />
                  {errors.micromarket && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.micromarket}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Google Maps URL"
                    value={formData.googleMapsUrl}
                    disabled
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Inventory Photos
                    </Typography>
                    {/* Drag-and-drop zone for multiple upload */}
                    <Box
                      sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        textAlign: 'center',
                        bgcolor: '#fafbfc',
                        cursor: 'pointer',
                      }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                        if (files.length === 0) return;
                        let uploaded = [];
                        setIsUploading(true);
                        for (const file of files) {
                          try {
                            const storageRef = ref(storage, `inventory_photos/${Date.now()}_${file.name}`);
                            await uploadBytes(storageRef, file);
                            const url = await getDownloadURL(storageRef);
                            uploaded.push({ url, type: '', description: '', file: null });
                          } catch (err) {
                            alert('Image upload failed: ' + err.message);
                          }
                        }
                        setPhotos(prev => {
                          const updated = [...prev, ...uploaded];
                          setFormData(f => ({ ...f, photos: updated.map(({ url, type, description }) => ({ url, type, description })) }));
                          return updated;
                        });
                        setIsUploading(false);
                      }}
                      onDragOver={e => e.preventDefault()}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        id="inventory-photo-multi-upload"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
                          if (files.length === 0) return;
                          let uploaded = [];
                          setIsUploading(true);
                          for (const file of files) {
                            try {
                              const storageRef = ref(storage, `inventory_photos/${Date.now()}_${file.name}`);
                              await uploadBytes(storageRef, file);
                              const url = await getDownloadURL(storageRef);
                              uploaded.push({ url, type: '', description: '', file: null });
                            } catch (err) {
                              alert('Image upload failed: ' + err.message);
                            }
                          }
                          setPhotos(prev => {
                            const updated = [...prev, ...uploaded];
                            setFormData(f => ({ ...f, photos: updated.map(({ url, type, description }) => ({ url, type, description })) }));
                            return updated;
                          });
                          setIsUploading(false);
                        }}
                      />
                      <label htmlFor="inventory-photo-multi-upload">
                        <Button 
                          variant="outlined" 
                          component="span" 
                          startIcon={isUploading ? <CircularProgress size={16} /> : <FiUpload />} 
                          sx={{ mb: 1, mx: 2 }}
                          disabled={isUploading}
                        >
                          {isUploading ? 'Uploading Images...' : 'Select Images'}
                        </Button>
                      </label>
                      <Typography variant="caption" color="textSecondary">
                        You can drag and drop multiple images, or click to select multiple files.
                      </Typography>
                    </Box>
                    {/* Add Another Photo Button - single upload */}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="inventory-photo-single-upload"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setIsUploading(true);
                        try {
                          const storageRef = ref(storage, `inventory_photos/${Date.now()}_${file.name}`);
                          await uploadBytes(storageRef, file);
                          const url = await getDownloadURL(storageRef);
                          setPhotos(prev => {
                            const updated = [...prev, { url, type: '', description: '', file: null }];
                            setFormData(f => ({ ...f, photos: updated.map(({ url, type, description }) => ({ url, type, description })) }));
                            return updated;
                          });
                        } catch (err) {
                          alert('Image upload failed: ' + err.message);
                        }
                        // Reset input value so same file can be re-uploaded if needed
                        e.target.value = '';
                        setIsUploading(false);
                      }}
                    />

                    {/* Table of photos - Card Style Design */}
                    {photos.map((photo, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 2,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          boxShadow: 0,
                          minHeight: 120
                        }}
                      >
                        {/* Image Upload/Preview Card */}
                        <Box
                          sx={{
                            width: 200,
                            height: 100,
                            border: '1px dashed #ccc',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#f7f7f7',
                            cursor: 'pointer',
                            mr: 1
                          }}
                          onClick={() => {
                            document.getElementById(`inventory-photo-upload-${index}`).click();
                          }}
                        >
                          {photo.url ? (
                            <img
                              src={photo.url}
                              alt={photo.type}
                              style={{ width: '70%', height: '70%', objectFit: 'contain', borderRadius: 6 }}
                            />
                          ) : (
                            <AddIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            id={`inventory-photo-upload-${index}`}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              setIsUploading(true);
                              try {
                                const storageRef = ref(storage, `inventory_photos/${Date.now()}_${file.name}`);
                                await uploadBytes(storageRef, file);
                                const url = await getDownloadURL(storageRef);
                                setPhotos(prev => {
                                  const updated = [...prev];
                                  updated[index] = { ...updated[index], url, file: null };
                                  setFormData(f => ({ ...f, photos: updated.map(({ url, type, description }) => ({ url, type, description })) }));
                                  return updated;
                                });
                              } catch (err) {
                                alert('Image upload failed: ' + err.message);
                              }
                              e.target.value = '';
                              setIsUploading(false);
                            }}
                          />
                        </Box>
                        {/* Photo Type Dropdown */}
                        <FormControl sx={{ minWidth: 160 }} size="small">
                          <Select
                            value={photo.type}
                            displayEmpty
                            onChange={e => handlePhotoChange(index, 'type', e.target.value)}
                          >
                            <MenuItem value="" disabled>
                              Photo Type
                            </MenuItem>
                            {photoTypes.map((type) => (
                              <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {/* Description Input */}
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Description"
                          value={photo.description}
                          onChange={e => handlePhotoChange(index, 'description', e.target.value)}
                          sx={{ minWidth: 280 }}
                        />
                        {/* Delete Button */}
                        <IconButton
                          onClick={() => handleRemovePhoto(index)}
                          color="error"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}

                    <label htmlFor="inventory-photo-single-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        sx={{ mt: 1 }}
                      >
                        Add Another Photo
                      </Button>
                    </label>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      sx={{
                        borderColor: theme?.palette.primary.main,
                        color: theme?.palette.primary.main,
                        '&:hover': {
                          borderColor: theme?.palette.primary.dark,
                          backgroundColor: 'transparent',
                        }
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                      sx={{
                        bgcolor: theme?.palette.primary.main,
                        '&:hover': {
                          bgcolor: theme?.palette.primary.dark
                        }
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        editMode ? 'Update ' : 'Add Inventory'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>

      <CenterForm
        open={openCenterForm}
        onClose={() => setOpenCenterForm(false)}
        theme={theme}
        operators={operators}
        proposalId={initialData?.proposalId}
        proposedOptions={initialData?.proposedOptions}
        editMode={true}
        initialData={{
          ...formData,
          id: formData.centerId,
          centerName: formData.centerName
        }}
        onSubmit={async (newCenter) => {
          setOpenCenterForm(false);
          if (onSubmit) {
            await onSubmit({
              ...formData,
              virtualTours: newCenter.virtualTours,
              centerName: newCenter.centerName,
              centerId: newCenter.id
            });
          }
        }}
      />
    </>
  );
};

export default InventoryForm;
